import { ChangeDetectorRef, Component, OnDestroy } from '@angular/core';
import { _error, _isDev, _isLab, _log } from '@shared/aux_helper_environment';
import { ChatRequest, DataAgentSSEService, SSEEvent } from 'app/services/agents/data-agent-sse.service';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-test-data-agent-sse',
  templateUrl: './test-data-agent-sse.component.html',
  styleUrls: ['./test-data-agent-sse.component.scss'],
})
export class TestDataAgentSSEComponent implements OnDestroy {
  // Request configuration
  agentType = 'main'; // 'main', 'insight', 'pricing', 'catman', 'strategy', 'data_import'
  conversationId = 'test-conv-001';
  message = 'Test message: Analiza las ventas del último trimestre';
  streamEnabled = true;

  // Component state
  isLoading = false;
  error: string | null = null;
  connectionState = 'CLOSED';
  events: SSEEvent[] = [];

  // Sistema mejorado de manejo de contenido
  currentReasoningText = ''; // Texto del ciclo actual en tiempo real
  finalOutput = ''; // Output final limpio cuando termine
  cycleHistory: Array<{ cycle: number; title: string; content: string }> = []; // Historial de ciclos
  debugInfo = ''; // Info de debugging

  // Estado actual
  currentCycle = 0;
  currentCycleTitle = '';
  pendingCycleReset = false; // Flag para indicar que el próximo contenido debe resetear

  // Estados de UI
  isStreamComplete = false;
  showCycleHistory = false; // Para mostrar/ocultar historial de ciclos
  hasValidOutput = false; // Para mantener output visible

  private sseSubscription?: Subscription;

  constructor(private sseService: DataAgentSSEService, private cdr: ChangeDetectorRef) {
    this.updateConnectionState();
  }

  ngOnDestroy(): void {
    this.stopStream();
  }

  async testSSEConnection() {
    if (!(_isDev() || _isLab())) {
      this.error = 'No se puede ejecutar en entorno que no sea de dev o lab';
      return;
    }

    this.isLoading = true;
    this.error = null;
    this.events = [];

    const request: ChatRequest = {
      message: this.message,
      conversation_id: this.conversationId,
      agent_type: this.agentType,
    };

    try {
      _log('StarSE stream with request:', request);

      this.sseSubscription = this.sseService.startDataAgentStreamWithFetch(request).subscribe({
        next: (event: SSEEvent) => {
          this.events.push(event);
          this.processEvent(event);
          _log('SSE Event received:', event);
          this.updateConnectionState();
        },
        error: (error: SSEEvent | Error) => {
          this.error = error instanceof Error ? error.message : (error as SSEEvent).error || 'Unknown error';
          _error('SSE Error:', error);
          this.isLoading = false;
          this.updateConnectionState();
        },
        complete: () => {
          _log('SSE Stream completed');
          this.isLoading = false;
          this.updateConnectionState();
        },
      });
    } catch (err: any) {
      this.error = err.message || 'Error al iniciar el stream SSE';
      _error('Test SSE error:', err);
      this.isLoading = false;
    }
  }

  stopStream(): void {
    if (this.sseSubscription) {
      this.sseSubscription.unsubscribe();
      this.sseSubscription = undefined;
    }

    this.sseService.closeConnection();
    this.isLoading = false;
    this.updateConnectionState();
    _log('SSE Stream stopped');
  }

  clearEvents(): void {
    this.events = [];
    this.clearMessages();
  }

  testConnection(): void {
    if (!(_isDev() || _isLab())) {
      this.error = 'No se puede ejecutar en entorno que no sea de dev o lab';
      return;
    }

    this.isLoading = true;
    this.error = null;
    this.events = [];

    this.sseSubscription = this.sseService.testConnection().subscribe({
      next: (event: SSEEvent) => {
        this.events.push(event);
        _log('Test connection event:', event);
        this.updateConnectionState();
      },
      error: (error: SSEEvent | Error) => {
        this.error = error instanceof Error ? error.message : (error as SSEEvent).error || 'Connection test failed';
        _error('Test connection error:', error);
        this.isLoading = false;
        this.updateConnectionState();
      },
      complete: () => {
        _log('Test connection completed');
        this.isLoading = false;
        this.updateConnectionState();
      },
    });
  }

  private updateConnectionState(): void {
    this.connectionState = this.sseService.getConnectionState();
  }

  getLastEvent(): SSEEvent | null {
    return this.events.length > 0 ? this.events[this.events.length - 1] : null;
  }

  getEventsByType(type: string): SSEEvent[] {
    return this.events.filter(event => event.type === type);
  }

  private processEvent(event: SSEEvent): void {
    // Debug logging
    _log('[processEvent] Processing event:', {
      type: event.type,
      content: event.content,
      text: event.text,
      message: event.message,
      currentReasoningLength: this.currentReasoningText.length,
    });

    // Normalizar el tipo de evento a lowercase para comparación
    const eventType = event.type?.toLowerCase();

    switch (eventType) {
      case 'init':
        // Evento de inicialización - solo para debugging
        if (event.message) {
          this.debugInfo += `[INIT] ${event.message}\n`;
        }
        break;
      case 'cycle_start':
        // Preparar nuevo ciclo, pero mantener el texto visible hasta que llegue contenido nuevo
        const newCycle = event.cycle || this.currentCycle + 1;
        const newCycleTitle = event.message || event.content || `Ciclo ${newCycle}`;

        // Guardar el ciclo anterior si tiene contenido significativo
        if (this.hasSignificantContent(this.currentReasoningText)) {
          _log(`[processEvent] Cycle ${this.currentCycle} has content, saving before starting cycle ${newCycle}`);
          this.savePreviousCycle();
          // NO resetear aquí - mantener texto visible hasta que llegue contenido nuevo
          this.pendingCycleReset = true; // Marcar que necesitamos resetear en el próximo contenido
        } else {
          _log(`[processEvent] Cycle ${this.currentCycle} has no significant content, not saving`);
          this.pendingCycleReset = false; // No hay nada que resetear
        }

        // Actualizar información del ciclo
        this.currentCycle = newCycle;
        this.currentCycleTitle = newCycleTitle;
        this.debugInfo += `\n[CYCLE ${this.currentCycle}] ${this.currentCycleTitle}\n`;

        _log(`[processEvent] Started new cycle ${this.currentCycle}: ${this.currentCycleTitle} (reset pending: ${this.pendingCycleReset})`);
        break;
      case 'text_chunk':
        // Los text_chunk se concatenan (texto en streaming)
        const textChunk = this.extractTextFromEvent(event);
        if (textChunk) {
          this.addTextToCurrentCycle(textChunk);
        }
        break;
      case 'reasoning':
        // Los reasoning reemplazan (contienen el texto completo procesado)
        const reasoningContent = this.extractTextFromEvent(event);
        if (reasoningContent) {
          this.replaceCurrentCycleContent(reasoningContent);
        }
        break;
      case 'tool_use':
        if (event.content || event.message) {
          this.debugInfo += `\n[TOOL USE] ${event.content || event.message}\n`;
        }
        break;
      case 'tool_result':
        if (event.content || event.message) {
          this.debugInfo += `[TOOL RESULT] ${event.content || event.message}\n`;
        }
        break;
      case 'complete':
        // Finalizar el stream - GARANTIZAR que el contenido se preserve
        this.isLoading = false;
        this.isStreamComplete = true;

        _log('[processEvent] STREAM COMPLETE - Preserving content:', {
          currentReasoningLength: this.currentReasoningText.length,
          currentReasoningPreview: this.currentReasoningText.substring(0, 100) + '...',
        });

        // GARANTIZAR que siempre tengamos contenido final - usar el contenido actual
        if (this.currentReasoningText.trim()) {
          // Usar directamente el contenido actual como output final
          this.finalOutput = this.currentReasoningText;
          this.hasValidOutput = true;
          _log('[processEvent] ✅ FINAL OUTPUT SET from current reasoning:', this.finalOutput.length);
        } else if (this.cycleHistory.length > 0) {
          // Si no hay contenido actual, usar el último ciclo guardado
          this.finalOutput = this.cycleHistory[this.cycleHistory.length - 1].content;
          this.hasValidOutput = true;
          _log('[processEvent] ✅ FINAL OUTPUT SET from last cycle:', this.finalOutput.length);
        } else {
          _log('[processEvent] ❌ WARNING: No content available for final output');
        }

        // Guardar el último ciclo para el historial
        if (this.hasSignificantContent(this.currentReasoningText)) {
          this.savePreviousCycle();
        }

        this.debugInfo += '\n[COMPLETE]';
        _log('[processEvent] ✅ Stream completed successfully');
        _log('[processEvent] Final state:', {
          finalOutputLength: this.finalOutput.length,
          hasValidOutput: this.hasValidOutput,
          cycleHistoryLength: this.cycleHistory.length,
        });
        break;
      case 'error':
        this.error = event.error || 'Unknown error occurred';
        this.isLoading = false;
        break;
    }
  }

  // Métodos auxiliares para el nuevo sistema
  private extractTextFromEvent(event: SSEEvent): string {
    return (
      event.content ||
      event.text ||
      event.message ||
      (event.data && typeof event.data === 'string' ? event.data : '') ||
      (event.data && event.data.text ? event.data.text : '') ||
      (event.data && event.data.content ? event.data.content : '')
    );
  }

  private addTextToCurrentCycle(text: string): void {
    if (!text) return;

    const textToAdd = text.trim();
    if (!textToAdd) return;

    // Si hay un reset pendiente (nuevo ciclo), resetear ahora que llega contenido real
    if (this.pendingCycleReset) {
      _log(
        `[addTextToCurrentCycle] 🔄 NEW CYCLE CONTENT: Resetting text for cycle ${this.currentCycle} - user saw previous content until now`
      );
      this.currentReasoningText = text; // Resetear con el nuevo contenido
      this.pendingCycleReset = false; // Reset completado
      _log('[addTextToCurrentCycle] ✅ Text reset complete, new length:', text.length);
    } else {
      // Lógica normal de acumulación de texto
      if (text.length > this.currentReasoningText.length + 100) {
        // El texto nuevo es mucho más largo, probablemente contiene todo el contenido
        this.currentReasoningText = text;
        _log('[addTextToCurrentCycle] Replaced full text, new length:', text.length);
      } else if (!this.currentReasoningText.includes(textToAdd)) {
        // Solo agregar si no está incluido ya
        this.currentReasoningText += text;
        _log('[addTextToCurrentCycle] Appended text, new length:', this.currentReasoningText.length);
      }
    }

    // Después de agregar texto real, marcar que este ciclo tiene contenido válido
    _log(`[addTextToCurrentCycle] Cycle ${this.currentCycle} now has ${this.currentReasoningText.length} characters`);
    this.cdr.detectChanges();
  }

  private replaceCurrentCycleContent(text: string): void {
    if (!text) return;

    _log(
      `[replaceCurrentCycleContent] 🔄 REPLACING content for cycle ${this.currentCycle} (old: ${this.currentReasoningText.length} chars, new: ${text.length} chars)`
    );

    // Reemplazar completamente el contenido actual
    this.currentReasoningText = text;

    // Si había un reset pendiente, completarlo
    if (this.pendingCycleReset) {
      this.pendingCycleReset = false;
      _log('[replaceCurrentCycleContent] ✅ Pending reset completed');
    }

    _log(`[replaceCurrentCycleContent] ✅ Content replaced, new length: ${this.currentReasoningText.length}`);
    this.cdr.detectChanges();
  }

  private hasSignificantContent(text: string): boolean {
    // Un ciclo tiene contenido significativo si tiene más de 10 caracteres
    // y no es solo espacios en blanco
    const trimmed = text.trim();
    return trimmed.length > 10;
  }

  private savePreviousCycle(): void {
    // Solo guardar si el ciclo tiene contenido significativo
    if (this.currentCycle > 0 && this.hasSignificantContent(this.currentReasoningText)) {
      const existingCycleIndex = this.cycleHistory.findIndex(c => c.cycle === this.currentCycle);

      if (existingCycleIndex >= 0) {
        // Actualizar ciclo existente
        this.cycleHistory[existingCycleIndex].content = this.currentReasoningText;
        this.cycleHistory[existingCycleIndex].title = this.currentCycleTitle;
        _log(`[savePreviousCycle] Updated existing cycle ${this.currentCycle}: ${this.currentCycleTitle}`);
      } else {
        // Agregar nuevo ciclo
        this.cycleHistory.push({
          cycle: this.currentCycle,
          title: this.currentCycleTitle,
          content: this.currentReasoningText,
        });
        _log(`[savePreviousCycle] Added new cycle ${this.currentCycle}: ${this.currentCycleTitle}`);
      }
    } else {
      _log(`[savePreviousCycle] Skipping cycle ${this.currentCycle} - no significant content`);
    }
  }

  // Métodos públicos actualizados
  getCurrentReasoningText(): string {
    // Si hay un reset pendiente, mostrar el texto actual con un indicador
    if (this.pendingCycleReset && this.isLoading) {
      return this.currentReasoningText; // Mantener texto visible mientras llega el nuevo
    }
    return this.currentReasoningText;
  }

  isPendingNewContent(): boolean {
    return this.pendingCycleReset && this.isLoading;
  }

  getReasoningMessage(): string {
    // Para compatibilidad, devolver el texto del ciclo actual
    return this.currentReasoningText;
  }

  getDebugInfo(): string {
    return this.debugInfo;
  }

  clearMessages(): void {
    this.currentReasoningText = '';
    this.finalOutput = '';
    this.debugInfo = '';
    this.currentCycle = 0;
    this.currentCycleTitle = '';
    this.cycleHistory = [];
    this.pendingCycleReset = false;
    this.isStreamComplete = false;
    this.showCycleHistory = false;
    this.hasValidOutput = false;
  }

  toggleCycleHistory(): void {
    this.showCycleHistory = !this.showCycleHistory;
  }

  getFinalOutput(): string {
    return this.finalOutput;
  }

  getCurrentCycleTitle(): string {
    return this.currentCycleTitle || `Ciclo ${this.currentCycle}`;
  }

  private extractFinalOutput(): void {
    // Buscar en todos los ciclos el que contiene el resultado final
    let allContent = '';

    // Combinar todo el contenido de los ciclos guardados
    this.cycleHistory.forEach(cycle => {
      allContent += cycle.content + '\n\n';
    });

    // SIEMPRE agregar el ciclo actual - es muy importante porque contiene la respuesta final
    if (this.currentReasoningText.trim()) {
      allContent += this.currentReasoningText;
      _log('[extractFinalOutput] Including current reasoning text:', this.currentReasoningText.length, 'characters');
    }

    // Buscar patrones de resultado final
    const patterns = [
      /##\s*[📊🔍📈📋📌].*Análisis.*$/m,
      /##\s*[📊🔍📈📋📌].*Completo.*$/m,
      /##\s*[📊🔍📈📋📌].*Resultado.*$/m,
      /##\s*[📊🔍📈📋📌].*Trimestre.*$/m,
    ];

    let headingMatch = null;
    let headingIndex = -1;

    // Buscar cualquiera de los patrones en todo el contenido
    for (const pattern of patterns) {
      const match = allContent.match(pattern);
      if (match) {
        const index = allContent.indexOf(match[0]);
        if (index > headingIndex) {
          headingMatch = match;
          headingIndex = index;
        }
      }
    }

    if (headingMatch && headingIndex >= 0) {
      this.finalOutput = allContent.substring(headingIndex);
    } else {
      // Si no hay heading específico, buscar desde el último ##
      const lastHeadingMatch = allContent.match(/##[^#]*$/s);
      if (lastHeadingMatch) {
        const lastIndex = allContent.lastIndexOf('##');
        this.finalOutput = allContent.substring(lastIndex);
      } else {
        // Si no hay headings específicos, usar todo el contenido disponible
        if (allContent.trim()) {
          this.finalOutput = allContent;
          _log('[extractFinalOutput] Using all content as final output');
        } else if (this.currentReasoningText.trim()) {
          this.finalOutput = this.currentReasoningText;
          _log('[extractFinalOutput] Using current reasoning as final output');
        } else if (this.cycleHistory.length > 0) {
          this.finalOutput = this.cycleHistory[this.cycleHistory.length - 1].content;
          _log('[extractFinalOutput] Using last cycle as final output');
        }
      }
    }

    _log('[extractFinalOutput] Final output extracted:', {
      totalCycles: this.cycleHistory.length,
      currentReasoningLength: this.currentReasoningText.length,
      allContentLength: allContent.length,
      finalLength: this.finalOutput.length,
      finalPreview: this.finalOutput.substring(0, 200) + '...',
    });
  }

  shouldShowRealtimeReasoning(): boolean {
    // SIEMPRE mostrar si hay contenido, hasta que haya un output final VÁLIDO
    const hasCurrentContent = this.currentReasoningText.length > 0;
    const hasFinalOutput = this.finalOutput && this.finalOutput.length > 0;

    // Solo ocultar el razonamiento en tiempo real si hay un output final válido Y completo
    const result = hasCurrentContent && !hasFinalOutput;

    _log('[shouldShowRealtimeReasoning]', {
      isLoading: this.isLoading,
      isStreamComplete: this.isStreamComplete,
      currentReasoningLength: this.currentReasoningText.length,
      finalOutputLength: this.finalOutput.length,
      hasCurrentContent,
      hasFinalOutput,
      result: result,
    });
    return result;
  }

  shouldShowFinalOutput(): boolean {
    // Solo mostrar output final si realmente existe Y es válido
    const result = this.hasValidOutput && this.finalOutput && this.finalOutput.length > 0;
    _log('[shouldShowFinalOutput]', {
      hasValidOutput: this.hasValidOutput,
      finalOutputLength: this.finalOutput?.length || 0,
      result: result,
    });
    return result;
  }

  getCycleHistory(): Array<{ cycle: number; title: string; content: string }> {
    return this.cycleHistory;
  }

  shouldShowCycleHistory(): boolean {
    return this.showCycleHistory && this.cycleHistory.length > 0;
  }

  trackByTimestamp(index: number, event: SSEEvent): number {
    return event.timestamp || index;
  }

  trackByCycle(index: number, cycle: { cycle: number; title: string; content: string }): number {
    return cycle.cycle;
  }

  // Método de debugging para entender el estado actual
  getDebugState(): any {
    return {
      isLoading: this.isLoading,
      isStreamComplete: this.isStreamComplete,
      hasValidOutput: this.hasValidOutput,
      currentReasoningLength: this.currentReasoningText.length,
      finalOutputLength: this.finalOutput.length,
      cycleHistoryLength: this.cycleHistory.length,
      pendingCycleReset: this.pendingCycleReset,
      shouldShowRealtime: this.shouldShowRealtimeReasoning(),
      shouldShowFinal: this.shouldShowFinalOutput(),
    };
  }
}
